import React, { FC, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";

interface PostIFMarkEmployeeDialogProps {
  open: boolean;
  applicantName?: string;
  onClose: () => void;
  onConfirm: (enterpriseEmployeeId: number) => void;
}

// HR converts an approved PostIF into an employee record by linking its Enterprise employee number.
// The number isn't held on the form, so HR types it here before we call mark-as-employee.
const PostIFMarkEmployeeDialog: FC<PostIFMarkEmployeeDialogProps> = ({ open, applicantName, onClose, onConfirm }) => {
  const [employeeNo, setEmployeeNo] = useState("");

  // Reset the field whenever the dialog reopens for a different row.
  useEffect(() => {
    if (open) setEmployeeNo("");
  }, [open]);

  const parsed = Number(employeeNo);
  const isValid = employeeNo.trim() !== "" && Number.isInteger(parsed) && parsed > 0;

  const handleConfirm = () => {
    if (isValid) onConfirm(parsed);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mark as employee</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {applicantName
            ? `Link ${applicantName}'s Post Interview Form to their employee record.`
            : "Link this Post Interview Form to the employee record."}{" "}
          Enter the Enterprise employee number.
        </Typography>
        <TextField
          // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: the modal's single input
          autoFocus
          fullWidth
          size="small"
          label="Employee #"
          value={employeeNo}
          onChange={(e) => setEmployeeNo(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          inputProps={{ inputMode: "numeric", "data-testid": "postif-mark-employee-input" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="postif-mark-employee-cancel">
          Cancel
        </Button>
        <Button variant="contained" disabled={!isValid} onClick={handleConfirm} data-testid="postif-mark-employee-confirm">
          Mark as employee
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostIFMarkEmployeeDialog;
